import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { useLocale } from "~/i18n";
import { fetchUnreadNotificationCount } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";

/** Keep the dot current while the app is in the foreground, like the other cards. */
const UNREAD_POLL_INTERVAL_MS = 60_000;

/**
 * The way into the notification centre, and the only place the app says "there is
 * something new" without being opened.
 *
 * Outlined rather than filled: the calendar button beside it is the header's primary
 * action, and two solid circles would compete. The count is never rendered as a
 * number — an inbox of three reminders is not a score, and a dot cannot be wrong by
 * one while a poll is in flight.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  // The watermark that decides "unread" lives on the account, so the query is keyed
  // by member; with nobody signed in there is nothing to ask for.
  const userId = useAuthStore((state) => state.user?.id) ?? "";

  const { data } = useQuery({
    queryKey: queryKeys.notificationsUnread(userId, locale),
    queryFn: ({ signal }) => fetchUnreadNotificationCount(locale, signal),
    enabled: userId.length > 0,
    refetchInterval: UNREAD_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
  });

  const unread = data?.unread_count ?? 0;

  return (
    <IconButton
      component={RouterLink}
      to="/notifications"
      aria-label={t("notifications.open")}
      data-testid="notifications.open"
      sx={{
        // The header's one auto margin: everything to the left of it stays left,
        // the bell and the calendar button ride the right edge together.
        marginLeft: "auto",
        width: 48,
        height: 48,
        color: "text.primary",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Badge
        variant="dot"
        color="primary"
        invisible={unread === 0}
        data-testid="notifications.badge"
      >
        <NotificationsNoneOutlinedIcon sx={{ fontSize: 24 }} />
      </Badge>
    </IconButton>
  );
}
